import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import MuiButton from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import Dropzone from '../shared/dropzone/Dropzone';
import Button from '../shared/Button';
import Input from '../shared/Input';
import PhoneInput from '../shared/PhoneInput';
import Alert from '../shared/Alert';
import LoadScreen from '../load-screen';
import Modal from './Modal';
import Enable2FAModal from './Enable2FAModal';
import EnableTOTPModal from './EnableTOTPModal';
import Disable2FAModal from './Disable2FAModal';
import UpdateEmailModal from './UpdateEmailModal';

import {
  updateUserData,
  savePushNotificationsToken,
  simpleUpdateUser,
} from '../../actions/userActions';

import { withFirebase } from '../../firebase';

import { api } from '../../util';

import urls from '../../constants/urls';
import { multiFactorAuthTypes } from '../../constants/enums';

import '../dashboard/dashboard.scss';

const UserProfileModal = (props) => {
  const {
    open,
    onClose,
    userFirstName,
    userLastName,
    userAddress,
    userPhone,
    uid,
    updateUser,
    loading,
    photoUrl,
    adminPassword,
    firebase,
    saveToken,
    currentUser,
    updateUserData,
    email,
  } = props;

  const [image, uploadImage] = useState([]);
  const [firstName, setFirstName] = useState(userFirstName || '');
  const [lastName, setLastName] = useState(userLastName || '');
  const [address, setAddress] = useState(userAddress || '');
  const [phone, setPhone] = useState(userPhone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const [enable2FAModalOpen, toggleEnable2FAModal] = useState(false);
  const [enableTOTPModalOpen, toggleEnableTOTPModal] = useState(false);
  const [disable2FAModalOpen, toggleDisable2FAModal] = useState(false);
  const [emailModalOpen, toggleEmailModal] = useState(false);
  const [enableSuccess, setEnableSuccess] = useState(false);
  const [disableSuccess, setDisableSuccess] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const handleSubmit = (e) => {
    if (e.keyCode === 13) {
    }
  };

  const onSubmit = () => {
    if (error) {
      return;
    }

    if (newPassword.length > 0 && newPassword.length < 4) {
      setError('Password is too short');

      return;
    }

    if (currentPassword.length && newPassword.length && currentPassword === newPassword) {
      setError('New password should be different from the current one');

      return;
    }

    const data = { firstName, lastName, address, phone };
    const profileImage = image && image.length ? image[0] : null;

    updateUser(uid, data, currentPassword, newPassword, profileImage).then(() => {
      setCurrentPassword('');
      setNewPassword('');
      onClose();
    });
  };

  const resetValues = () => {
    uploadImage([]);
    setFirstName(userFirstName || '');
    setLastName(userLastName || '');
    setAddress(userAddress || '');
    setPhone(userPhone || '');
    setCurrentPassword('');
    setNewPassword('');
  };

  const getImageUrl = () => {
    if (image && image.length) {
      return image[0].preview;
    }
    if (photoUrl) {
      return photoUrl;
    }
    return 'default_user.svg';
  };

  const requestPermission = () => {
    if ('Notification' in window) {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          setInfoMessage('Notification permission granted. You can reset notification permissions in your browser settings.');
          firebase.getNotificationToken().then(saveToken);
        } else {
          setInfoMessage('Notification permission denied');
        }
      });
    } else {
      if ('safari' in window && 'pushNotification' in window.safari) {
        // Safari on iOS
        window.safari.pushNotification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            setInfoMessage('Notification permission granted. You can reset notification permissions in your browser settings.');
            firebase.getNotificationToken().then(saveToken);
          } else {
            setInfoMessage('Notification permission denied');
          }
        });
      } else {
        setInfoMessage('Notification API is not enabled');
      }
    }
  };

  const testPushNotification = async () => {
    setTimeout(() => {
      api.post(urls.sendPushNotification, { uid, title: 'Test Title', body: 'Test message' });
    }, 1500);
  };

  const onEnableSMSSuccess = () => {
    updateUserData({ multiFactorEnabled: true, multiFactorType: multiFactorAuthTypes.SMS });
    setEnableSuccess(true);
  };

  const onEnableTOTPSuccess = () => {
    updateUserData({ multiFactorEnabled: true, multiFactorType: multiFactorAuthTypes.TOTP });
    setEnableSuccess(true);
  };

  const onDisable2FASuccess = () => {
    updateUserData({ multiFactorEnabled: false, multiFactorType: null });
    setDisableSuccess(true);
  };

  const onEmailUpdatedSuccess = (email) => {
    updateUserData({ email });
    setEmailSuccess(true);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="p-lg">
            {loading && <LoadScreen />}
            <div className="d-flex p-b-md">
              <div className="avatar-container">
                <img src={getImageUrl()} alt="avatar" />
              </div>
              <Dropzone
                multiple={false}
                files={image}
                onUpload={uploadImage}
                type="image"
                text="Drag and drop your file here or click to upload"
                hideFilesList
              />
            </div>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  id="firstName"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onKeyDown={handleSubmit}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  id="lastName"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onKeyDown={handleSubmit}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  id="address"
                  type="text"
                  placeholder="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onKeyDown={handleSubmit}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                />
              </Grid>
            </Grid>

            <Grid container spacing={0}>
              <Grid size={{ xs: 12 }}>
                <div className="d-flex p-t-md p-r-md" style={{ alignItems: 'center' }}>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    disabled
                    fullWidth
                  />
                  <div className="m-l-sm">
                    <IconButton aria-label="edit" color="primary" size="small" onClick={() => toggleEmailModal(true)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              </Grid>
            </Grid>
            <div className="" style={{ marginTop: 12, marginBottom: 4 }}>
              Update Password
            </div>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="password-input">
                  <Input
                    id="currentPassword"
                    type="password"
                    placeholder="Current password"
                    value={currentPassword}
                    onChange={(e) => {
                      setError(null);
                      setCurrentPassword(e.target.value);
                    }}
                    onKeyDown={handleSubmit}
                    fullWidth
                  />
                </div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <div className="password-input">
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => {
                      setError(null);
                      setNewPassword(e.target.value);
                    }}
                    onKeyDown={handleSubmit}
                    fullWidth
                  />
                </div>
              </Grid>
            </Grid>
            {adminPassword && (
              <div className="d-flex align-center m-t-md">
                <WarningIcon htmlColor="#ee9321" />
                <div className="m-l-sm">
                  You have default password set by admin. Please change to a new one.
                </div>
              </div>
            )}
            <div className="actions d-flex space-between" style={{ marginTop: 20, alignItems: 'flex-end' }}>
              <Typography color='secondary'>Version 4.0.1</Typography>
              <Button type="submit" onClick={() => onSubmit()} text="Save Changes" />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="p-lg">
            <Grid>
              <div>
                <div className="font-bold">Notification Permissions</div>
                <div className="font-size-sm secondary m-t-md m-b-md">Click the button below to reset your permissions
                  for all notifications from this app. You will need to allow notifications when prompted if you wish to
                  receive them in the future.
                </div>
                <MuiButton onClick={requestPermission} variant="outlined" color="primary"
                           style={{ minWidth: 240, borderRadius: 12 }}>
                  RESET PERMISSIONS
                </MuiButton>
              </div>
              <div className="m-t-lg border-bottom"></div>
              <div className="m-t-md">
                <div className="font-bold">Send Test Notification</div>
                <div className="font-size-sm secondary m-t-md m-b-md">Click the button below send a test notification to
                  your account. This button will trigger iOS, Android or In-app notifications depending on your device
                  and status.
                </div>

                <MuiButton onClick={testPushNotification} variant="outlined" color="primary"
                           style={{ minWidth: 240, borderRadius: 12 }}>
                  SEND TEST
                </MuiButton>
              </div>
            </Grid>
          </div>
        );
      case 2:
        return (
          <div className="p-lg">
            <div className="font-bold">Two-Factor Authentication</div>
            <div className="d-flex m-t-md">
              <div className="">Active multi-factor authentication type:</div>
              {currentUser?.multiFactorEnabled ? <div
                  className="m-l-sm primary">{currentUser.multiFactorType === multiFactorAuthTypes.TOTP ? 'Authenticator' : 'SMS'}</div> :
                <div className="m-l-sm danger">Disabled</div>}
            </div>
            <div className="font-size-sm m-t-md secondary">We recommend adding additional security to your account via a
              two factor authentication process, either via SMS or a passcode to your favourite authenticator app.
            </div>
            <div className="font-size-sm m-t-md m-b-md secondary">Click one of the below buttons to begin enrollment of
              2FA.
            </div>
            <div className="d-flex">
              <div className="m-r-md">
                <MuiButton
                  onClick={() => toggleEnable2FAModal(true)}
                  variant="outlined"
                  color="primary"
                  style={{ minWidth: 240, borderRadius: 12 }}
                  disabled={currentUser?.multiFactorEnabled && currentUser?.multiFactorType === multiFactorAuthTypes.SMS}
                >
                  ENABLE SMS 2FA
                </MuiButton>
              </div>
              <div className="m-l-md">
                <MuiButton
                  onClick={() => toggleEnableTOTPModal(true)}
                  variant="outlined"
                  color="primary"
                  style={{ minWidth: 240, borderRadius: 12 }}
                  disabled={currentUser?.multiFactorEnabled && currentUser?.multiFactorType === multiFactorAuthTypes.TOTP}
                >
                  ENABLE Authenticator 2FA
                </MuiButton>
              </div>
            </div>

            {/*{!!currentUser?.multiFactorEnabled && (*/}
            {/*  <div className="d-flex align-start m-t-lg">*/}
            {/*    <WarningIcon style={{ fill: '#ee9321' }} />*/}
            {/*    <div style={{ color: '#ee9321', marginLeft: '4px' }}>To change multi factor authentication type please disable previous one first by clicking on "Disable 2FA" button</div>*/}
            {/*  </div>*/}
            {/*)}*/}

            {/*<div className='m-t-lg border-bottom'></div>*/}

            <div className="m-t-md">
              <div className="font-bold">Disable Additional Authentication</div>
              <div className="font-size-sm m-t-md m-b-md secondary">Click the button below to permanently remove any
                enabled 2FA.
              </div>
              <MuiButton
                onClick={() => {
                  if (currentUser?.multiFactorEnabled) {
                    toggleDisable2FAModal(true);
                  }
                }}
                color="primary"
                variant="outlined"
                style={{ minWidth: 240, borderRadius: 12, color: '#d32f2f', borderColor: '#d32f2f' }}
              >
                DISABLE 2FA
              </MuiButton>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          resetValues();
          onClose();
        }}
        onSubmit={onSubmit}
        title="Personal Information"
        tabs={['Information', 'Notifications', 'Authentication']}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {renderContent()}
        <Alert variant="error" message={error} open={!!error} onClose={() => setError(null)} />
        <Alert variant="warning" message={infoMessage} open={!!infoMessage} onClose={() => setInfoMessage(null)} />

        <Enable2FAModal
          open={enable2FAModalOpen}
          onClose={() => toggleEnable2FAModal(false)}
          onSuccess={onEnableSMSSuccess}
        />
        <EnableTOTPModal
          open={enableTOTPModalOpen}
          onClose={() => toggleEnableTOTPModal(false)}
          onSuccess={onEnableTOTPSuccess}
        />
        <Disable2FAModal
          open={disable2FAModalOpen}
          onClose={() => toggleDisable2FAModal(false)}
          onSuccess={onDisable2FASuccess}
        />
        <UpdateEmailModal
          open={emailModalOpen}
          onClose={() => toggleEmailModal(false)}
          onSuccess={onEmailUpdatedSuccess}
        />
      </Modal>
      <Alert variant="success" message="2FA successfully enabled" open={enableSuccess}
             onClose={() => setEnableSuccess(false)} />
      <Alert variant="success" message="2FA successfully disabled" open={disableSuccess}
             onClose={() => setDisableSuccess(false)} />
      <Alert variant="success" message="A verification link has been sent to your email account" open={emailSuccess}
             onClose={() => setEmailSuccess(false)} />
    </>
  );
};

UserProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userAddress: PropTypes.string,
  userPhone: PropTypes.string,
  adminPassword: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    uid: state.user.currentUser.uid,
    photoUrl: state.user.currentUser.photoUrl,
    email: state.user.currentUser.email,
    loading: state.user.loading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (uid, data, currentPassword, newPassword, image) =>
      dispatch(updateUserData(uid, data, currentPassword, newPassword, image)),
    saveToken: (token) => dispatch(savePushNotificationsToken(token)),
    updateUserData: (data) => dispatch(simpleUpdateUser(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withFirebase(UserProfileModal));
